// API Configuration
// This file centralizes all API endpoint configurations
// Environment variables take precedence over default values

interface ApiConfig {
  bookService: string;
  userService: string;
  videoService: string;
  adminService: string;
  contentStructuresService: string;
  contentAssetsService: string;
  packageService: string;
  blogService: string;
  faqService: string;
  feedbackService: string;
  gradesService: string;
  hashtagsService: string;
  couponService: string;
  paymentService: string;
  successStoryService: string;
  apiGateway?: string;
}

// Service URLs from environment variables with fallback defaults
const getServiceUrl = (envVar: string, serviceName: string, fallback: string): string => {
  const url = process.env[envVar];
  if (!url) {
    console.warn(`Environment variable ${envVar} not set for ${serviceName}. Using fallback: ${fallback}`);
    return fallback;
  }
  return url;
};

// Get configuration from environment variables with fallbacks
const getApiConfig = (): ApiConfig => {
  return {
    bookService: process.env.REACT_APP_BOOK_SERVICE_URL || (() => { throw new Error('REACT_APP_BOOK_SERVICE_URL environment variable is required') })(),
    userService: process.env.REACT_APP_USER_SERVICE_URL || (() => { throw new Error('REACT_APP_USER_SERVICE_URL environment variable is required') })(),
    videoService: process.env.REACT_APP_VIDEO_SERVICE_URL || (() => { throw new Error('REACT_APP_VIDEO_SERVICE_URL environment variable is required') })(),
    adminService: process.env.REACT_APP_ADMIN_SERVICE_URL || (() => { throw new Error('REACT_APP_ADMIN_SERVICE_URL environment variable is required') })(),
    contentStructuresService: process.env.REACT_APP_CONTENT_STRUCTURES_SERVICE_URL || (() => { throw new Error('REACT_APP_CONTENT_STRUCTURES_SERVICE_URL environment variable is required') })(),
    contentAssetsService: process.env.REACT_APP_CONTENT_ASSETS_SERVICE_URL || (() => { throw new Error('REACT_APP_CONTENT_ASSETS_SERVICE_URL environment variable is required') })(),
    packageService: process.env.REACT_APP_PACKAGE_SERVICE_URL || (() => { throw new Error('REACT_APP_PACKAGE_SERVICE_URL environment variable is required') })(),
    blogService: process.env.REACT_APP_BLOG_SERVICE_URL || (() => { throw new Error('REACT_APP_BLOG_SERVICE_URL environment variable is required') })(),
    faqService: process.env.REACT_APP_FAQ_SERVICE_URL || (() => { throw new Error('REACT_APP_FAQ_SERVICE_URL environment variable is required') })(),
    feedbackService: process.env.REACT_APP_FEEDBACK_SERVICE_URL || (() => { throw new Error('REACT_APP_FEEDBACK_SERVICE_URL environment variable is required') })(),
    gradesService: process.env.REACT_APP_GRADES_SERVICE_URL || (() => { throw new Error('REACT_APP_GRADES_SERVICE_URL environment variable is required') })(),
    hashtagsService: process.env.REACT_APP_HASHTAGS_SERVICE_URL || (() => { throw new Error('REACT_APP_HASHTAGS_SERVICE_URL environment variable is required') })(),
    couponService: process.env.REACT_APP_COUPON_SERVICE_URL || (() => { throw new Error('REACT_APP_COUPON_SERVICE_URL environment variable is required') })(),
    paymentService: process.env.REACT_APP_PAYMENT_SERVICE_URL || (() => { throw new Error('REACT_APP_PAYMENT_SERVICE_URL environment variable is required') })(),
    successStoryService: process.env.REACT_APP_SUCCESS_STORY_SERVICE_URL || '',
    apiGateway: process.env.REACT_APP_API_GATEWAY_URL || undefined
  };
};

// Export the configuration
export const API_CONFIG = getApiConfig();

// Convenience functions for building API URLs
export const apiUrls = {
  // Book Service endpoints
  books: {
    list: `${API_CONFIG.bookService}/api/books`,
    admin: `${API_CONFIG.bookService}/api/books/admin`,
    subjects: `${API_CONFIG.bookService}/api/books/subjects`,
    subjectsAdmin: `${API_CONFIG.bookService}/api/books/subjects/admin`,
    subjectsWithGrades: `${API_CONFIG.bookService}/api/books/subjects/admin/with-grades`,
    subjectById: (id: number) => `${API_CONFIG.bookService}/api/books/subjects/admin/${id}`,
    subjectGrades: (subjectId: number) => `${API_CONFIG.bookService}/api/books/subjects/${subjectId}/grades`,
    linkSubjectGrades: (subjectId: number) => `${API_CONFIG.bookService}/api/books/subjects/${subjectId}/grades`,
    unlinkSubjectGrade: (subjectId: number, gradeId: number) => `${API_CONFIG.bookService}/api/books/subjects/${subjectId}/grades/${gradeId}`,
    grades: `${API_CONFIG.bookService}/api/books/grades`,
    gradesAdmin: `${API_CONFIG.bookService}/api/books/grades/admin`,
    allSubjectGrades: `${API_CONFIG.bookService}/api/books/subject-grades`
  },
  
  // Blog endpoints
  blogs: {
    list: `${API_CONFIG.blogService}/api/blogs`,
    admin: `${API_CONFIG.blogService}/api/blogs/admin`,
    stats: `${API_CONFIG.blogService}/api/blogs/stats`,
    byId: (id: number) => `${API_CONFIG.blogService}/api/blogs/${id}`,
    create: `${API_CONFIG.blogService}/api/blogs/admin`,
    update: (id: number) => `${API_CONFIG.blogService}/api/blogs/admin/${id}`,
    delete: (id: number) => `${API_CONFIG.blogService}/api/blogs/admin/${id}`,
    incrementViews: (id: number) => `${API_CONFIG.blogService}/api/blogs/${id}/increment-views`,
    uploadImage: `${API_CONFIG.blogService}/api/blogs/upload-image`,
    uploadInlineImage: `${API_CONFIG.blogService}/api/blogs/upload-inline-image`,
    uploadContentImage: `${API_CONFIG.blogService}/api/blogs/upload-content-image`
  },
  // Hashtags/Tags endpoints
  hashtags: {
    list: `${API_CONFIG.hashtagsService}/api/hashtags`,
    admin: `${API_CONFIG.hashtagsService}/api/hashtags/admin`,
    popular: `${API_CONFIG.hashtagsService}/api/hashtags/popular`,
    categories: `${API_CONFIG.hashtagsService}/api/hashtags/categories`,
    stats: `${API_CONFIG.hashtagsService}/api/hashtags/stats`,
    bySlug: (slug: string) => `${API_CONFIG.hashtagsService}/api/hashtags/${slug}`,
    blogTags: (blogId: number) => `${API_CONFIG.hashtagsService}/api/hashtags/blogs/${blogId}/tags`,
    addTagsToBlog: (blogId: number) => `${API_CONFIG.hashtagsService}/api/hashtags/blogs/${blogId}/tags`,
    updateBlogTags: (blogId: number) => `${API_CONFIG.hashtagsService}/api/hashtags/blogs/${blogId}/tags`,
    removeTagFromBlog: (blogId: number, tagId: number) => `${API_CONFIG.hashtagsService}/api/hashtags/blogs/${blogId}/tags/${tagId}`,
    generate: `${API_CONFIG.hashtagsService}/api/hashtags/generate`,
    socialHashtags: (platform: string, blogId: number) => `${API_CONFIG.hashtagsService}/api/hashtags/social/${platform}/${blogId}`
},  


  // FAQ endpoints
  faqs: {
    list: `${API_CONFIG.faqService}/api/faqs`,
    admin: `${API_CONFIG.faqService}/api/faqs/admin`,
    search: `${API_CONFIG.faqService}/api/faqs/search`,
    categories: `${API_CONFIG.faqService}/api/faqs/admin/categories`,
    stats: `${API_CONFIG.faqService}/api/faqs/admin/stats`,
    byId: (id: number) => `${API_CONFIG.faqService}/api/faqs/admin/${id}`,
    create: `${API_CONFIG.faqService}/api/faqs/admin`,
    update: (id: number) => `${API_CONFIG.faqService}/api/faqs/admin/${id}`,
    delete: (id: number) => `${API_CONFIG.faqService}/api/faqs/admin/${id}`,
    publish: (id: number) => `${API_CONFIG.faqService}/api/faqs/admin/${id}/publish`,
    unpublish: (id: number) => `${API_CONFIG.faqService}/api/faqs/admin/${id}/unpublish`,
    reorder: `${API_CONFIG.faqService}/api/faqs/admin/reorder`
  },
  
  // Feedback endpoints
  feedback: {
    submit: `${API_CONFIG.feedbackService}/api/feedback`,
    types: `${API_CONFIG.feedbackService}/api/feedback/types`,
    admin: `${API_CONFIG.feedbackService}/api/feedback/admin`,
    stats: `${API_CONFIG.feedbackService}/api/feedback/admin/stats`,
    byId: (id: number) => `${API_CONFIG.feedbackService}/api/feedback/admin/${id}`,
    updateStatus: (id: number) => `${API_CONFIG.feedbackService}/api/feedback/admin/${id}/status`,
    updatePriority: (id: number) => `${API_CONFIG.feedbackService}/api/feedback/admin/${id}/priority`,
    delete: (id: number) => `${API_CONFIG.feedbackService}/api/feedback/admin/${id}`
  },
  
  // Country and location endpoints
  countries: {
    list: `${API_CONFIG.bookService}/api/countries`,
    admin: `${API_CONFIG.bookService}/api/countries/admin`,
    byId: (id: number) => `${API_CONFIG.bookService}/api/countries/admin/${id}`,
    create: `${API_CONFIG.bookService}/api/countries/admin`,
    update: (id: number) => `${API_CONFIG.bookService}/api/countries/admin/${id}`,
    delete: (id: number) => `${API_CONFIG.bookService}/api/countries/admin/${id}`,
    states: (countryId: string | number) => `${API_CONFIG.bookService}/api/countries/${countryId}/states`,
    statesAdmin: `${API_CONFIG.bookService}/api/countries/states/admin`,
    stateById: (id: number) => `${API_CONFIG.bookService}/api/countries/states/admin/${id}`,
    createState: `${API_CONFIG.bookService}/api/countries/states/admin`,
    updateState: (id: number) => `${API_CONFIG.bookService}/api/countries/states/admin/${id}`,
    deleteState: (id: number) => `${API_CONFIG.bookService}/api/countries/states/admin/${id}`
  },
  
  // Video Service endpoints - Updated for consolidated video learning management
  videos: {
    // Core video management
    list: `${API_CONFIG.videoService}/api/videos`,
    admin: `${API_CONFIG.videoService}/api/videos/admin`,
    videoById: (id: number) => `${API_CONFIG.videoService}/api/videos/admin/${id}`,
    upload: `${API_CONFIG.videoService}/api/videos/upload`,
    generateSignedUrl: `${API_CONFIG.videoService}/api/videos/signed-url`,
    
    // Legacy endpoints (keep for backward compatibility)
    packages: `${API_CONFIG.videoService}/api/videos/packages-test`,
    grades: `${API_CONFIG.videoService}/api/videos/grades`,
    gradesAdmin: `${API_CONFIG.videoService}/api/videos/grades/admin`,
    packageById: (id: number) => `${API_CONFIG.videoService}/api/videos/packages-test/${id}`,
    gradeById: (id: number) => `${API_CONFIG.videoService}/api/videos/grades/admin/${id}`
  },

  // Content Management endpoints - Managed through video service
  content: {
    list: `${API_CONFIG.videoService}/api/content`,
    byId: (id: number) => `${API_CONFIG.videoService}/api/content/${id}`,
    search: `${API_CONFIG.videoService}/api/content/search`,
    create: `${API_CONFIG.videoService}/api/content`,
    update: (id: number) => `${API_CONFIG.videoService}/api/content/${id}`,
    delete: (id: number) => `${API_CONFIG.videoService}/api/content/${id}`
  },

  // Video Service endpoints for content structures
  video: {
    // Content structures - Direct to content-structures-service
    contentStructures: `${API_CONFIG.contentStructuresService}/api/content-structures`,
    structureContentLinks: `${API_CONFIG.videoService}/api/structure-content-links`,
    bookContentLinks: `${API_CONFIG.videoService}/api/book-content-links`,
    contentUsage: `${API_CONFIG.videoService}/api/content-usage`,
    // Content assets (reuse existing endpoint)
    content: `${API_CONFIG.videoService}/api/content`
  },

  // Content Library endpoints (using content-structures-service)
  contentLibrary: {
    // Content structures
    contentStructures: `${API_CONFIG.contentStructuresService}/api/content-structures`,
    contentStructureById: (id: number) => `${API_CONFIG.contentStructuresService}/api/content-structures/${id}`,
    contentStructureChildren: (id: number) => `${API_CONFIG.contentStructuresService}/api/content-structures/${id}/children`,
    structureContentLinks: (structureId: number) => `${API_CONFIG.contentStructuresService}/api/content-structures/${structureId}/content-links`,
    bookContentLinks: `${API_CONFIG.contentStructuresService}/api/book-content-links`,
    contentUsage: `${API_CONFIG.contentStructuresService}/api/content-usage`,
    // Reordering
    reorderContentStructures: `${API_CONFIG.contentStructuresService}/api/content-structures/reorder`
  },

  // Content Assets endpoints (using dedicated content-assets-service)
  contentAssets: {
    list: `${API_CONFIG.contentAssetsService}/api/content-assets`,
    byId: (id: number) => `${API_CONFIG.contentAssetsService}/api/content-assets/${id}`,
    byGrade: (gradeId: number) => `${API_CONFIG.contentAssetsService}/api/content-assets/by-grade/${gradeId}`,
    bySubject: (subjectId: number) => `${API_CONFIG.contentAssetsService}/api/content-assets/by-subject/${subjectId}`
  },

  // Video Learning Management System endpoints - Book-First Workflow
  videoLearning: {
    // Books (for selection and filtering) - using books admin endpoint (returns book_details structure)
    books: `${API_CONFIG.bookService}/api/books/admin`,
    
    // Video Types
    videoTypes: `${API_CONFIG.videoService}/api/video-learning/video-types`,
    videoTypeById: (id: number) => `${API_CONFIG.videoService}/api/video-learning/video-types/${id}`,
    
    // Book-based hierarchy endpoints
    bookStructure: (bookId: number) => `${API_CONFIG.videoService}/api/video-learning/book-structure/${bookId}`,
    
    // Unified Content Structure API (replaces old topics/chapters/units/lessons)
    contentStructures: `${API_CONFIG.bookService}/api/content-structures`,
    contentStructureById: (id: number) => `${API_CONFIG.bookService}/api/content-structures/${id}`,
    contentStructureChildren: (id: number) => `${API_CONFIG.bookService}/api/content-structures/${id}/children`,
    createContentStructure: `${API_CONFIG.bookService}/api/content-structures`,
    updateContentStructure: (id: number) => `${API_CONFIG.bookService}/api/content-structures/${id}`,
    deleteContentStructure: (id: number) => `${API_CONFIG.bookService}/api/content-structures/${id}`,
    reorderContentStructures: `${API_CONFIG.bookService}/api/content-structures/reorder`,
    
    // Content Assets and Links
    contentAssets: `${API_CONFIG.bookService}/api/content`,
    structureContentLinks: `${API_CONFIG.bookService}/api/structure-content-links`,
    bookContentLinks: `${API_CONFIG.bookService}/api/book-content-links`,
    contentUsage: `${API_CONFIG.bookService}/api/content-usage`,
    
    // Video Upload & Management (now content-asset-scoped)
    uploadVideo: `${API_CONFIG.bookService}/api/content/upload`,
    contentAssetById: (id: number) => `${API_CONFIG.bookService}/api/content/${id}`,
    generateSignedUrl: (id: number) => `${API_CONFIG.bookService}/api/content/${id}/signed-url`,
    
    // Search and filtering
    searchContent: `${API_CONFIG.bookService}/api/content/search`,
    filterContent: `${API_CONFIG.bookService}/api/content/filter`,
    
    // Analytics
    contentAnalytics: `${API_CONFIG.bookService}/api/content-structures/analytics`
  },
  
  // User Service endpoints
  users: {
    auth: `${API_CONFIG.userService}/api/auth`,
    profile: `${API_CONFIG.userService}/api/users/profile`,
    admin: `${API_CONFIG.userService}/api/auth/admin/users`,
    general: `${API_CONFIG.userService}/api/auth/users`
  },
  
  // Admin Service endpoints
  admin: {
    dashboard: `${API_CONFIG.adminService}/api/admin/dashboard`,
    users: `${API_CONFIG.adminService}/api/admin/users`
  },
  
  // Book Service additional endpoints
  booksExtra: {
    subjectById: (id: number) => `${API_CONFIG.bookService}/api/books/subjects/admin/${id}`,
    bookById: (id: number) => `${API_CONFIG.bookService}/api/books/admin/${id}`
  },
  
  // Academy Service endpoints  
  academy: {
    // Courses endpoints
    courses: `${API_CONFIG.bookService}/api/academy/courses`,
    coursesOnline: `${API_CONFIG.bookService}/api/academy/courses?mode=online`,
    coursesInPerson: `${API_CONFIG.bookService}/api/academy/courses?mode=in-person`,
    courseDetails: (courseId: number) => `${API_CONFIG.bookService}/api/academy/courses/${courseId}/details`,
    
    // Batches endpoints
    batches: `${API_CONFIG.bookService}/api/academy/batches`,
    batchesWithCourses: `${API_CONFIG.bookService}/api/academy/batches-with-courses`,
    batchesByCourse: (courseId: number) => `${API_CONFIG.bookService}/api/academy/courses/${courseId}/batches`,
    batchStudents: (batchId: number) => `${API_CONFIG.bookService}/api/academy/batches/${batchId}/students`,
    
    // Combined endpoints for dashboard data
    onlineClassesWithEnrollment: `${API_CONFIG.bookService}/api/academy/online-classes-with-enrollment`,
    inPersonClassesWithEnrollment: `${API_CONFIG.bookService}/api/academy/inperson-classes-with-enrollment`,
    
    // Admin endpoints
    onlineClassesAdmin: `${API_CONFIG.bookService}/api/academy/online-classes/admin`,
    inPersonClassesAdmin: `${API_CONFIG.bookService}/api/academy/inperson-classes/admin`,
    batchesAdmin: `${API_CONFIG.bookService}/api/academy/batches/admin`,
    batchStudentsAdmin: `${API_CONFIG.bookService}/api/academy/batch-students/admin`,
    
    // Individual record endpoints
    courseById: (id: number) => `${API_CONFIG.bookService}/api/academy/courses/${id}`,
    batchById: (id: number) => `${API_CONFIG.bookService}/api/academy/batches/${id}`,
    enrollmentById: (id: number) => `${API_CONFIG.bookService}/api/academy/enrollments/${id}`,
    
    // CRUD endpoints for courses
    createCourse: `${API_CONFIG.bookService}/api/academy/courses`,
    updateCourse: (id: number) => `${API_CONFIG.bookService}/api/academy/courses/${id}`,
    deleteCourse: (id: number) => `${API_CONFIG.bookService}/api/academy/courses/${id}`
  },

  // Success Stories endpoints (independent service)
  successStories: {
    list: `${API_CONFIG.successStoryService}/api/success-stories`,
    admin: `${API_CONFIG.successStoryService}/api/success-stories/admin`,
    create: `${API_CONFIG.successStoryService}/api/success-stories/admin`,
    update: (id: number) => `${API_CONFIG.successStoryService}/api/success-stories/admin/${id}`,
    delete: (id: number) => `${API_CONFIG.successStoryService}/api/success-stories/admin/${id}`,
    toggle: (id: number) => `${API_CONFIG.successStoryService}/api/success-stories/admin/${id}/toggle`
  },

  // Package Service endpoints
  packages: {
    list: `${API_CONFIG.packageService}/api/packages`,
    byId: (id: number) => `${API_CONFIG.packageService}/api/packages/${id}`,
    create: `${API_CONFIG.packageService}/api/packages`,
    update: (id: number) => `${API_CONFIG.packageService}/api/packages/${id}`,
    delete: (id: number) => `${API_CONFIG.packageService}/api/packages/${id}`,
    approve: (id: number) => `${API_CONFIG.packageService}/api/packages/${id}/approval`
  },

  // Subscription Service endpoints
  subscriptions: {
    list: `${API_CONFIG.packageService}/api/subscriptions`,
    byUser: (userId: number) => `${API_CONFIG.packageService}/api/subscriptions/user/${userId}`,
    userContent: (userId: number) => `${API_CONFIG.packageService}/api/subscriptions/user/${userId}/content`,
    create: `${API_CONFIG.packageService}/api/subscriptions`,
    updateStatus: (id: number) => `${API_CONFIG.packageService}/api/subscriptions/${id}/status`,
    cancel: (id: number) => `${API_CONFIG.packageService}/api/subscriptions/${id}`
  },

  // Coupon Service endpoints
  coupons: {
    validate: `${API_CONFIG.couponService}/api/coupons/validate`,
    apply: `${API_CONFIG.couponService}/api/coupons/apply`,
    admin: `${API_CONFIG.couponService}/api/coupons/admin`,
    byId: (id: number) => `${API_CONFIG.couponService}/api/coupons/admin/${id}`,
    create: `${API_CONFIG.couponService}/api/coupons/admin`,
    update: (id: number) => `${API_CONFIG.couponService}/api/coupons/admin/${id}`,
    delete: (id: number) => `${API_CONFIG.couponService}/api/coupons/admin/${id}`,
    activate: (id: number) => `${API_CONFIG.couponService}/api/coupons/admin/${id}/activate`,
    pause: (id: number) => `${API_CONFIG.couponService}/api/coupons/admin/${id}/pause`,
    resume: (id: number) => `${API_CONFIG.couponService}/api/coupons/admin/${id}/resume`,
    schedule: (id: number) => `${API_CONFIG.couponService}/api/coupons/admin/${id}/schedule`,
    expire: (id: number) => `${API_CONFIG.couponService}/api/coupons/admin/${id}/expire`,
    archive: (id: number) => `${API_CONFIG.couponService}/api/coupons/admin/${id}/archive`,
    usage: (id: number) => `${API_CONFIG.couponService}/api/coupons/admin/${id}/usage`,
    history: (id: number) => `${API_CONFIG.couponService}/api/coupons/admin/${id}/history`,
    refund: (usageId: number) => `${API_CONFIG.couponService}/api/coupons/admin/usage/${usageId}/refund`,
    statsOverview: `${API_CONFIG.couponService}/api/coupons/admin/stats/overview`,
    expiringSoon: `${API_CONFIG.couponService}/api/coupons/admin/expiring-soon`
  },

  // Payment Service endpoints
  payments: {
    createOrder: `${API_CONFIG.paymentService}/api/payments/create-order`,
    capturePayment: (paymentId: string) => `${API_CONFIG.paymentService}/api/payments/capture/${paymentId}`,
    getOrder: (orderNumber: string) => `${API_CONFIG.paymentService}/api/payments/order/${orderNumber}`,
    userOrders: (userId: number) => `${API_CONFIG.paymentService}/api/payments/user/${userId}/orders`,
    testCapture: `${API_CONFIG.paymentService}/api/payments/test/capture`
  },

  // Grades Service endpoints
  grades: {
    list: `${API_CONFIG.gradesService}/api/grades`,
    admin: `${API_CONFIG.gradesService}/api/grades/admin/list`,
    create: `${API_CONFIG.gradesService}/api/grades/admin`,
    update: (id: number) => `${API_CONFIG.gradesService}/api/grades/admin/${id}`,
    delete: (id: number) => `${API_CONFIG.gradesService}/api/grades/admin/${id}`,
    byId: (id: number) => `${API_CONFIG.gradesService}/api/grades/${id}`
  }
};

// HTTP client configuration with default headers
export const getHttpHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

// Environment info for debugging
export const getEnvironmentInfo = () => {
  return {
    environment: process.env.REACT_APP_ENVIRONMENT || 'development',
    config: API_CONFIG,
    nodeEnv: process.env.NODE_ENV
  };
};

console.log('🔧 API Configuration loaded:', getEnvironmentInfo());
